import { FUNCTION_DIAGNOSIS_PRIORITIES, OPERATION_INDEXES } from "core/consts";
import {
  ExactKeys,
  FunctionDiagnosisPriority,
  GKVMedicalFormData,
  ValueOf,
} from "core/types";
import { convertModelDefinition, valueDef } from "react-forms-state";

const valueDefFunctionalDiagnosis = (priority: FunctionDiagnosisPriority) => {
  return {
    ...valueDef(`icd_code_${priority}_diagnosis`),
    ...valueDef(`icd_code_${priority}_code`),
    ...valueDef(`icd_code_${priority}_reason`),
    ...valueDef(`icd_code_${priority}_side_localization_primary`),
  } as const;
};

export type OperationIndex = ValueOf<typeof OPERATION_INDEXES>;
const valueDefOperation = (index: OperationIndex) => {
  return {
    ...valueDef(`operation_${index}_date`),
    ...valueDef(`operation_${index}_ops`),
    ...valueDef(`operation_${index}_wound_closed`),
  } as const;
};

const model = {
  ...valueDef("insurance_ik_number"),
  ...valueDef("hospital_ik_number"),

  // section one
  ...valueDef("gender"),
  ...valueDef("patient_first_name"),
  ...valueDef("patient_last_name"),
  ...valueDef("insurance_number"),
  ...valueDef("patient_birthdate"),
  ...valueDef("admission_date"),
  ...valueDef("discharge_date"),
  ...valueDef("early_rehabilitation_measures"),
  ...valueDef("rehab_admission_type"),
  ...valueDef("rehab_admission_within_two_weeks_start_date"),
  ...valueDef("rehab_admission_outside_two_weeks_start_date"),
  ...valueDef("rehab_admission_outside_two_weeks_explanation"),

  // section two
  ...valueDefFunctionalDiagnosis(FUNCTION_DIAGNOSIS_PRIORITIES.PRIO_1),
  ...valueDefFunctionalDiagnosis(FUNCTION_DIAGNOSIS_PRIORITIES.PRIO_2),
  ...valueDefFunctionalDiagnosis(FUNCTION_DIAGNOSIS_PRIORITIES.PRIO_3),
  ...valueDefFunctionalDiagnosis(FUNCTION_DIAGNOSIS_PRIORITIES.PRIO_4),
  ...valueDefFunctionalDiagnosis(FUNCTION_DIAGNOSIS_PRIORITIES.PRIO_5),

  ...valueDef("surgery_performed"),
  ...valueDefOperation(OPERATION_INDEXES.OP1),
  ...valueDefOperation(OPERATION_INDEXES.OP2),
  ...valueDefOperation(OPERATION_INDEXES.OP3),
  ...valueDefOperation(OPERATION_INDEXES.OP4),
  ...valueDefOperation(OPERATION_INDEXES.OP5),
  ...valueDef("other_treatments"),
  ...valueDef("complication_in_treatment_process"),
  ...valueDef("complication_cause_maindiagnosis"),
  ...valueDef("complication_cause_maindiagnosis_description"),
  ...valueDef("complication_cause_cardiovascular"),
  ...valueDef("complication_cause_cardiovascular_description"),
  ...valueDef("complication_cause_other"),
  ...valueDef("complication_cause_other_description"),
  ...valueDef("infections_state"),
  ...valueDef("infections"),

  ...valueDef("singer_assessment_food"),
  ...valueDef("singer_assessment_dressing_and_undressing"),
  ...valueDef("singer_assessment_hygiene"),
  ...valueDef("singer_assessment_shower"),
  ...valueDef("singer_assessment_stool"),
  ...valueDef("singer_assessment_urination"),
  ...valueDef("singer_assessment_toilet_use"),
  ...valueDef("singer_assessment_transfer"),
  ...valueDef("singer_assessment_wheelchair"),
  ...valueDef("singer_assessment_walking"),
  ...valueDef("singer_assessment_stairs"),
  ...valueDef("singer_assessment_listening"),
  ...valueDef("singer_assessment_talking"),
  ...valueDef("singer_assessment_reading"),
  ...valueDef("singer_assessment_writing"),
  ...valueDef("singer_assessment_orientation"),
  ...valueDef("singer_assessment_concentration"),
  ...valueDef("singer_assessment_planning"),
  ...valueDef("singer_assessment_social_interaction"),
  ...valueDef("singer_assessment_domestic_life_household"),
  ...valueDef("singer_assessment_date"),

  // section three
  ...valueDef("limited_capacity"),
  ...valueDef("reha_capacity"),
  ...valueDef("reha_capacity_partial"),
  ...valueDef("interim_prosthesis_fitted"),
  ...valueDef("reha_capacity_other"),
  ...valueDef("reha_capacity_early_rehab_criteria"),

  // section four
  ...valueDef("rehabilitation_goals"),

  // section five
  ...valueDef("rehab_focus"),
  ...valueDef("rehab_focus_other_description"),
  ...valueDef("recommended_reha_type"),
  ...valueDef("recommended_reha_type_reason"),
  ...valueDef("rehab_directive_state"),
  ...valueDef("rehab_directive_type"),

  // section six
  ...valueDef("patient_body_height"),
  ...valueDef("patient_body_weight"),
  ...valueDef("special_requirements_required"),
  ...valueDef("special_requirements"),
  ...valueDef("special_requirements_dialysis_description"),
  ...valueDef("special_requirements_weight_bed_description"),
  ...valueDef("special_requirements_relatives_description"),
  ...valueDef("special_requirements_decubitus_description"),
  ...valueDef("special_requirements_other_description"),

  ...valueDef("transport_type"),
  ...valueDef("transport_type_taxi_specification"),
  ...valueDef("transport_type_ambulance_specification"),
  ...valueDef("transport_type_ambulance_description"),
  ...valueDef("require_accompanying_person"),
  ...valueDef("doctor_in_charge_in_hospital_first_name"),
  ...valueDef("doctor_in_charge_in_hospital_last_name"),
  ...valueDef("doctor_in_charge_in_hospital_phone"),
  ...valueDef("doctor_exam_date"),
  ...valueDef("approved_by"),
} as const;

type TopLevelModelKeys = Extract<keyof typeof model, keyof GKVMedicalFormData>;

type _CheckExtraKeys = ExactKeys<GKVMedicalFormData, TopLevelModelKeys>;

export const GKVmedicalFormDefinition = convertModelDefinition(model);
